import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { HiArrowLeft, HiHandThumbUp, HiHandThumbDown, HiShare } from 'react-icons/hi2';
import { useAuth } from '../hooks/useAuth';
import ImageViewer from '../components/post/ImageViewer';
import AnnotationToolbar from '../components/post/AnnotationToolbar';
import AnnotationPopup from '../components/post/AnnotationPopup';
import CommentsSection from '../components/post/CommentsSection';
import InterpretationsSection from '../components/post/InterpretationsSection';
import LoadingSpinner from '../components/common/LoadingSpinner';
import api from '../services/api';
import toast from 'react-hot-toast';

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interpretations, setInterpretations] = useState([]);
  const [comments, setComments] = useState([]);
  const [interpretationComments, setInterpretationComments] = useState([]);
  const [reactionLoading, setReactionLoading] = useState(false);
  const { user } = useAuth();

  const [activeAnnotationTool, setActiveAnnotationTool] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentAnnotation, setCurrentAnnotation] = useState(null);
  const [showAnnotationForm, setShowAnnotationForm] = useState(false);
  const [hoveredInterpretation, setHoveredInterpretation] = useState(null);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [annotationTitle, setAnnotationTitle] = useState('');
  const [annotationDescription, setAnnotationDescription] = useState('');
  const [interpretationPage, setInterpretationPage] = useState(0);
  const [hasMoreInterpretations, setHasMoreInterpretations] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const imageRef = useRef(null);

  // Mobile detection with resize listener
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchPostDetails = async () => {
    try {
      const response = await api.get(`/post/${postId}`);
      setPost(response.data);

      // Reset interpretation state
      setInterpretations([]);
      setInterpretationComments([]);
      setInterpretationPage(0);
      setHasMoreInterpretations(true);

      // Load first page of interpretations
      const interpretationRes = await api.get(`/interpretations/post/${postId}?skip=0&limit=5`);
      const firstPage = interpretationRes.data;
      setInterpretations(firstPage);
      setInterpretationPage(1);
      if (firstPage.length < 5) setHasMoreInterpretations(false);

      // Fetch comments for first page
      const allInterpretationComments = [];
      setLoading(false);
    } catch (error) {
      console.error('Error fetching post details:', error);
      toast.error('Failed to load post details');
      setLoading(false);
    }
  };

  const loadMoreInterpretations = async () => {
    try {
      const res = await api.get(`/interpretations/post/${postId}?skip=${interpretationPage * 5}&limit=5`);
      const newItems = res.data;

      if (newItems.length < 5) setHasMoreInterpretations(false);
      setInterpretations(prev => [...prev, ...newItems]);
      setInterpretationPage(prev => prev + 1);

    } catch (err) {
      console.error('Failed to load more interpretations', err);
      toast.error('Failed to load more interpretations');
    }
  };

  const handleFetchInterpretationComments = async (interpretationId) => {
    try {
      const response = await api.get(`/comments/interpretation/${interpretationId}?include_nested=true`);
      setInterpretationComments(prev => {
        const alreadyExists = prev.find(c => c.interpretationId === interpretationId);
        if (alreadyExists) return prev;
        return [...prev, {
          interpretationId,
          comments: response.data.comments,
        }];
      });
    } catch (error) {
      console.error('Error fetching interpretation comments:', error);
      toast.error('Failed to load comments');
    }
  };

  useEffect(() => {
    fetchPostDetails();
  }, [postId]);

  const handleReaction = async (type) => {
    if (!user) {
      toast.error('Please log in to react to posts');
      return;
    }

    if (reactionLoading) return;

    // Store previous state for rollback
    const previousPost = { ...post };
    setReactionLoading(true);

    try {
      // Optimistic update
      let newPost = { ...post };

      if (post.user_reaction === type) {
        // Remove reaction
        newPost.user_reaction = null;
        if (type === 'LIKE') {
          newPost.like_count = Math.max(0, newPost.like_count - 1);
        } else {
          newPost.dislike_count = Math.max(0, newPost.dislike_count - 1);
        }
      } else {
        // Add or change reaction
        const previousReaction = post.user_reaction;
        newPost.user_reaction = type;

        // If changing from one reaction to another
        if (previousReaction) {
          if (previousReaction === 'LIKE') {
            newPost.like_count = Math.max(0, newPost.like_count - 1);
          } else {
            newPost.dislike_count = Math.max(0, newPost.dislike_count - 1);
          }
        }

        // Add new reaction count
        if (type === 'LIKE') {
          newPost.like_count = newPost.like_count + 1;
        } else {
          newPost.dislike_count = newPost.dislike_count + 1;
        }
      }

      // Update UI immediately
      setPost(newPost);

      // Make API call
      if (previousPost.user_reaction === type) {
        // Remove reaction
        await api.delete(`/post/${postId}/reactions`);
      } else {
        // Add or change reaction
        await api.post(`/post/${postId}/reactions`, { type });
      }

      // Optionally fetch fresh data to ensure consistency
      setTimeout(() => {
        fetchPostDetails();
      }, 500);

    } catch (error) {
      // Rollback optimistic update
      setPost(previousPost);

      console.error('Error reacting to post:', error);

      // More specific error messages
      if (error.response?.status === 401) {
        toast.error('Please log in to react to posts');
      } else if (error.response?.status === 404) {
        toast.error('Post not found');
      } else if (error.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment.');
      } else {
        toast.error('Failed to react. Please try again.');
      }
    } finally {
      setReactionLoading(false);
    }
  };

  const interpretationRefs = useRef({});

  const handleViewFullInterpretation = (id) => {
    const el = interpretationRefs.current[id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring', 'ring-blue-500');
      setTimeout(() => {
        el.classList.remove('ring', 'ring-blue-500');
      }, 5000);
    }
  };

  const handleViewTag = (id) => {
    const match = interpretations.find((i) => i.id === id);
    if (!match) return;
    const imgTop = imageRef.current.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: imgTop - (isMobile ? 50 : 100), behavior: 'smooth' });

    setHoveredInterpretation(match);
    setTimeout(() => setHoveredInterpretation(null), 5000);
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: post.title,
          text: post.content,
          url: url
        });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard!');
      } catch (clipboardError) {
        toast.error('Failed to share or copy link');
      }
    }
  };

  const handleToolSelect = (tool) => {
    setActiveAnnotationTool(activeAnnotationTool === tool ? null : tool);
    setCurrentAnnotation(null);
  };

  const handleDeleteComment = (commentId) => {
    // Remove comment from local state
    setComments(prevComments =>
      prevComments.filter(comment => comment.id !== commentId)
    );
  };

  const handleToggleAnnotations = () => setShowAnnotations(!showAnnotations);

  const handleAnnotationStart = (e) => {
    if (!activeAnnotationTool || !user) return;
    const rect = imageRef.current.getBoundingClientRect();
    
    // Handle touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    setIsDrawing(true);
    setCurrentAnnotation({
      type: activeAnnotationTool,
      startX: x,
      startY: y,
      endX: x,
      endY: y,
    });
  };

  const handleAnnotationMove = (e) => {
    if (!isDrawing || !currentAnnotation) return;
    const rect = imageRef.current.getBoundingClientRect();
    
    // Handle touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    setCurrentAnnotation((prev) => ({
      ...prev,
      endX: x,
      endY: y,
    }));
  };

  const handleAnnotationEnd = () => {
    if (!isDrawing || !currentAnnotation) return;
    setIsDrawing(false);
    setShowAnnotationForm(true);
  };

  const handleSaveAnnotation = async () => {
    if (!annotationTitle.trim() || !annotationDescription.trim() || !currentAnnotation) return;

    const image = imageRef.current;
    const rect = image.getBoundingClientRect();
    const renderedWidth = rect.width;
    const renderedHeight = rect.height;

    const normalize = (value, dimension) => value / dimension;

    let shapeData;

    if (currentAnnotation.type === 'box') {
      const x = Math.min(currentAnnotation.startX, currentAnnotation.endX);
      const y = Math.min(currentAnnotation.startY, currentAnnotation.endY);
      const width = Math.abs(currentAnnotation.endX - currentAnnotation.startX);
      const height = Math.abs(currentAnnotation.endY - currentAnnotation.startY);

      shapeData = {
        x: normalize(x, renderedWidth),
        y: normalize(y, renderedHeight),
        width: normalize(width, renderedWidth),
        height: normalize(height, renderedHeight),
        title: annotationTitle,
      };
    } else {
      shapeData = {
        startX: normalize(currentAnnotation.startX, renderedWidth),
        startY: normalize(currentAnnotation.startY, renderedHeight),
        endX: normalize(currentAnnotation.endX, renderedWidth),
        endY: normalize(currentAnnotation.endY, renderedHeight),
        title: annotationTitle,
      };
    }

    const payload = {
      shape_type: currentAnnotation.type === 'box' ? 'BOX' : 'LINE',
      shape_data: shapeData,
      text: annotationDescription,
    };

    try {
      await api.post(`/interpretations/${postId}`, payload);
      await fetchPostDetails();
      setCurrentAnnotation(null);
      setShowAnnotationForm(false);
      setAnnotationTitle('');
      setAnnotationDescription('');
      setActiveAnnotationTool(null);
      toast.success('Annotation saved successfully!');
    } catch (error) {
      console.error('Error saving annotation:', error);
      toast.error('Failed to save annotation. Please try again.');
    }
  };

  const handleAddInterpretation = async (content) => {
    try {
      const payload = {
        shape_type: 'BOX',
        shape_data: { title: 'General Interpretation' },
        text: content,
      };
      await api.post(`/interpretations/${postId}`, payload);
      await fetchPostDetails();
      toast.success('Interpretation added successfully!');
    } catch (error) {
      console.error('Error adding interpretation:', error);
      toast.error('Failed to add interpretation. Please try again.');
    }
  };

  const handleAddInterpretationComment = async (content, parentId = null, interpretationId) => {
    try {
      const payload = {
        content,
        interpretation_id: interpretationId,
        parent_id: parentId,
      };
      await api.post('/comments/', payload);
      await fetchPostDetails();
      toast.success('Comment added successfully!');
    } catch (error) {
      console.error('Error adding interpretation comment:', error);
      toast.error('Failed to add comment. Please try again.');
    }
  };

  const handleAddComment = async (content, parentId = null, interpretationId = null) => {
    try {
      const payload = {
        content,
        post_id: interpretationId ? null : postId,
        interpretation_id: interpretationId,
        parent_id: parentId,
      };
      await api.post('/comments/', payload);
      await fetchPostDetails();
      toast.success('Comment added successfully!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment. Please try again.');
    }
  };

  const handleLikeInterpretation = async (interpretationId) => {
    if (!user) {
      toast.error('Please log in to like interpretations');
      return;
    }
    try {
      await api.post(`/interpretations/${interpretationId}/like`);
      await fetchPostDetails();
    } catch (error) {
      console.error('Error liking interpretation:', error);
      toast.error('Failed to like interpretation. Please try again.');
    }
  };

  const handleDislikeInterpretation = async (interpretationId) => {
    if (!user) {
      toast.error('Please log in to dislike interpretations');
      return;
    }
    try {
      await api.post(`/interpretations/${interpretationId}/dislike`);
      await fetchPostDetails();
    } catch (error) {
      console.error('Error disliking interpretation:', error);
      toast.error('Failed to dislike interpretation. Please try again.');
    }
  };

  if (loading || !post) return <LoadingSpinner />;

  return (
    <div className="flex flex-col bg-black text-white">
      <div className="container mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 py-4 sm:py-6 lg:py-8">
        {/* Back Button - Responsive sizing */}
        <motion.button
          onClick={() => navigate('/forum')}
          className="flex items-center gap-1.5 sm:gap-2 mb-4 sm:mb-6 px-3 sm:px-4 py-2 sm:py-2.5 bg-gray-800/50 border border-gray-700 rounded-lg text-gray-300 hover:text-white hover:bg-gray-700/50 transition-all duration-200 text-sm sm:text-base touch-manipulation"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <HiArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
          <span className="font-medium">Back to Forum</span>
        </motion.button>

        {/* Post Header - Responsive typography and spacing */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-2 sm:mb-3 leading-tight">
            {post.title}
          </h1>
          <p className="text-xs sm:text-sm lg:text-base text-gray-400 mb-2 sm:mb-3">
            Posted by u/{post.author.name} • {new Date(post.created_at).toLocaleDateString()}
          </p>
          {post.content && (
            <p className="text-sm sm:text-base lg:text-lg text-gray-300 leading-relaxed">
              {post.content}
            </p>
          )}

          {/* Reaction Buttons - Enhanced mobile responsiveness */}
          <div className="mt-3 sm:mt-4 flex flex-wrap gap-2 sm:gap-3 lg:gap-4 text-xs sm:text-sm text-gray-400 items-center">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => handleReaction('LIKE')}
              disabled={reactionLoading}
              className={`flex items-center gap-1 sm:gap-1.5 transition-all duration-200 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full touch-manipulation min-w-0 ${
                post.user_reaction === 'LIKE'
                  ? 'text-blue-400 bg-blue-400/10 border border-blue-400/20'
                  : 'hover:text-blue-400 hover:bg-blue-400/5'
              } ${reactionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <HiHandThumbUp className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${reactionLoading ? 'animate-pulse' : ''}`} />
              <span className="font-medium">Like</span>
              {post.like_count > 0 && (
                <span className="text-xs bg-gray-700 px-1 sm:px-1.5 py-0.5 rounded-full min-w-0 flex-shrink-0">
                  {post.like_count}
                </span>
              )}
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => handleReaction('DISLIKE')}
              disabled={reactionLoading}
              className={`flex items-center gap-1 sm:gap-1.5 transition-all duration-200 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full touch-manipulation min-w-0 ${
                post.user_reaction === 'DISLIKE'
                  ? 'text-red-400 bg-red-400/10 border border-red-400/20'
                  : 'hover:text-red-400 hover:bg-red-400/5'
              } ${reactionLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <HiHandThumbDown className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${reactionLoading ? 'animate-pulse' : ''}`} />
              <span className="font-medium">Dislike</span>
              {post.dislike_count > 0 && (
                <span className="text-xs bg-gray-700 px-1 sm:px-1.5 py-0.5 rounded-full min-w-0 flex-shrink-0">
                  {post.dislike_count}
                </span>
              )}
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={handleShare}
              className="flex items-center gap-1 sm:gap-1.5 hover:text-green-400 hover:bg-green-400/5 px-2 sm:px-3 py-1.5 sm:py-2 rounded-full transition-all duration-200 touch-manipulation min-w-0"
            >
              <HiShare className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="font-medium">Share</span>
            </motion.button>
          </div>
        </div>

        {/* Main Content Area - Responsive layout */}
        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex-1">
            {/* Annotation Toolbar and Image Viewer - Responsive layout */}
            <div className={`flex w-full h-full ${isMobile ? 'flex-col gap-3' : 'gap-4'}`}>
              {['ADMIN', 'ANALYST', 'MODERATOR', 'BLOG_WRITER'].includes(user?.role?.toUpperCase()) && (
                <div className={isMobile ? 'order-2' : 'flex-shrink-0'}>
                  <AnnotationToolbar
                    activeAnnotationTool={activeAnnotationTool}
                    onToolSelect={handleToolSelect}
                    showAnnotations={showAnnotations}
                    onToggleAnnotations={handleToggleAnnotations}
                    user={user}
                  />
                </div>
              )}
              <div className={`flex-1 ${isMobile ? 'order-1' : ''}`}>
                <ImageViewer
                  post={post}
                  interpretations={interpretations}
                  currentAnnotation={currentAnnotation}
                  hoveredInterpretation={hoveredInterpretation}
                  imageRef={imageRef}
                  activeAnnotationTool={activeAnnotationTool}
                  isDrawing={isDrawing}
                  showAnnotations={showAnnotations}
                  onAnnotationStart={handleAnnotationStart}
                  onAnnotationMove={handleAnnotationMove}
                  onAnnotationEnd={handleAnnotationEnd}
                  onInterpretationHover={setHoveredInterpretation}
                  onLikeInterpretation={handleLikeInterpretation}
                  onDislikeInterpretation={handleDislikeInterpretation}
                  onViewFullInterpretation={handleViewFullInterpretation}
                  user={user}
                />
              </div>
            </div>
          </div>

          {/* Comments and Interpretations - Enhanced responsive grid */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
            {/* Discussion Section */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-5 shadow-md backdrop-blur-sm order-2 xl:order-1">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-200 mb-3 sm:mb-4">
                Discussion
              </h2>
              <CommentsSection
                postId={post.id}
                interpretations={interpretations}
                comments={comments}
                onAddComment={handleAddComment}
                onLikeInterpretation={handleLikeInterpretation}
                onDislikeInterpretation={handleDislikeInterpretation}
                onFetchInterpretationComments={handleFetchInterpretationComments}
                onDeleteComment={handleDeleteComment}
                user={user}
              />
            </div>

            {/* Interpretations Section */}
            <div className="bg-gray-800/40 border border-gray-700 rounded-xl sm:rounded-2xl p-3 sm:p-4 lg:p-5 shadow-md backdrop-blur-sm order-1 xl:order-2">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-200 mb-3 sm:mb-4">
                Interpretations
              </h2>
              
              <InterpretationsSection
                interpretations={interpretations}
                interpretationComments={interpretationComments}
                onAddInterpretation={handleAddInterpretation}
                onAddInterpretationComment={handleAddInterpretationComment}
                onLikeInterpretation={handleLikeInterpretation}
                onDislikeInterpretation={handleDislikeInterpretation}
                onRefresh={fetchPostDetails}
                interpretationRefs={interpretationRefs}
                user={user}
                onFetchInterpretationComments={handleFetchInterpretationComments}
                onViewTag={handleViewTag}
              />

              {/* Load More Button - Enhanced mobile styling */}
              {hasMoreInterpretations && (
                <div className="mt-4 sm:mt-6 text-center">
                  <motion.button
                    onClick={loadMoreInterpretations}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-primary text-white rounded-lg sm:rounded-xl hover:bg-blue-600 transition-all duration-200 font-medium text-sm sm:text-base touch-manipulation"
                  >
                    Load More Interpretations
                  </motion.button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Annotation Popup - Enhanced for mobile */}
      <AnimatePresence>
        {showAnnotationForm && (
          <AnnotationPopup
            title={annotationTitle}
            description={annotationDescription}
            onTitleChange={setAnnotationTitle}
            onDescriptionChange={setAnnotationDescription}
            onSave={handleSaveAnnotation}
            onCancel={() => {
              setShowAnnotationForm(false);
              setCurrentAnnotation(null);
              setAnnotationTitle('');
              setAnnotationDescription('');
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default PostDetail;